import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Trash2 } from 'lucide-react';
import { idemApi } from '../lib/api';
import { clearIdemDeviceToken } from '../lib/idemSession';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function StatusBadge({ status }) {
  const tone = {
    pending: 'border-amber-300 bg-amber-50 text-amber-800',
    approved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    denied: 'border-red-300 bg-red-50 text-red-700',
    expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'pending').replaceAll('_', ' ')}</span>;
}

function formatSessionError(value) {
  if (!value) return '';
  if (value === 'digital_key_authenticator_session_not_found') {
    return 'Stored device token could not be verified. Use Open account and Forget this device if needed.';
  }
  return value;
}

export default function IdemRequestsInbox() {
  const [session, setSession] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionError, setSessionError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await idemApi.get('/idem/security');
      setSession(data.session || null);
      setRequests((data.recent_requests || []).filter((request) => request.status === 'pending'));
      setSessionError(data.session_error || '');
    } catch (requestError) {
      setSession(null);
      setRequests([]);
      setSessionError('');
      setError(requestError?.response?.data?.error || 'The paired request inbox could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <main className="min-h-screen bg-zinc-100 px-4 py-6 text-zinc-900 sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-4xl">
        <header className="flex items-center gap-2.5 border-b border-zinc-200 pb-5">
          <div className="grid h-9 w-9 place-items-center bg-black text-sm font-semibold text-white">i</div>
          <div>
            <p className="font-display text-lg font-bold tracking-tight">idem</p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Request inbox</span>
          </div>
        </header>

        <section className="mt-5 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-medium">Internal lab only</p>
              <p className="mt-1 text-xs leading-5">This request inbox shows only the paired account&apos;s safe internal lab requests. It does not expose raw evidence, public login sessions, or production OIDC/FAS/CSAM behavior.</p>
            </div>
          </div>
        </section>

        {sessionError && <div className="mt-5 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{formatSessionError(sessionError)}</div>}
        {error && <div className="mt-5 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Loading request inbox…</div>
        ) : !session ? (
          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <p className="font-display text-xl font-semibold tracking-tight text-zinc-900">Activation required</p>
            <p className="mt-2 max-w-2xl text-sm text-zinc-600">Open an activation link to pair this authenticator. Once paired, pending requests for the same digital key account will appear here.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/idem/account" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open account</Link>
              <Link to="/idem/security" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open security</Link>
              <button type="button" onClick={() => { clearIdemDeviceToken(); setSession(null); setRequests([]); setSessionError(''); setError(''); }} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                Forget this device
              </button>
            </div>
          </section>
        ) : (
          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Paired account</p>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Pending requests</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-600">These requests belong to the paired digital key account only.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/idem/account" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open account</Link>
                <Link to="/idem/security" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open security</Link>
              </div>
            </div>

            {session.status !== 'active' && (
              <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                This paired device is no longer active. Requests are read-only until a new device is paired.
              </div>
            )}

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <article className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device</p>
                <p className="mt-1 text-sm text-zinc-700">{session.device_label}</p>
                <p className="mt-1 font-mono text-xs text-zinc-500">{session.device_public_id}</p>
              </article>
              <article className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Account</p>
                <p className="mt-1 break-all font-mono text-xs text-zinc-700">{session.digital_key_account_id}</p>
                <p className="mt-1 text-xs text-zinc-500">{session.identity_display_name || session.identity_public_id || '—'}</p>
              </article>
            </div>

            {requests.length ? (
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3 font-medium">Request</th>
                      <th className="py-2 pr-3 font-medium">Identity</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Assurance</th>
                      <th className="py-2 font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} className="border-b border-zinc-100 last:border-b-0">
                        <td className="py-3 pr-3 font-mono text-xs text-zinc-700">{request.id}</td>
                        <td className="py-3 pr-3 text-xs text-zinc-600">{request.identity_display_name || request.identity_public_id || request.identity_id || '—'}</td>
                        <td className="py-3 pr-3"><StatusBadge status={request.status} /></td>
                        <td className="py-3 pr-3 text-xs text-zinc-600">{String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</td>
                        <td className="whitespace-nowrap py-3 text-xs text-zinc-500">{formatDate(request.updated_at || request.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-5 border border-dashed border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
                No pending requests are waiting for this paired account.
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
